import React, { useState, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  CloudArrowUpIcon,
  XMarkIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import { fetchAuthSession } from 'aws-amplify/auth';

// Supported file types
const SUPPORTED_EXTENSIONS = new Set([
  // JPEG
  'jpg', 'jpeg', 'jpe', 'jfif',
  // RAW formats
  'cr2', 'cr3', 'nef', 'nrw', 'arw', 'dng', 'orf', 'rw2',
  'pef', 'srw', 'raf', 'raw', 'rwl', 'iiq', '3fr', 'fff',
  // Other
  'png', 'tif', 'tiff', 'heic', 'heif'
]);

interface UploadFile {
  file: File;
  id: string;
  status: 'pending' | 'uploading' | 'success' | 'error';
  progress: number;
  error?: string;
  contentHash?: string;
}

interface PhotoUploaderProps {
  onComplete?: (uploadedCount: number) => void;
  onUploadStart?: () => void;
  maxConcurrent?: number;
  className?: string;
}

export const PhotoUploader: React.FC<PhotoUploaderProps> = ({
  onComplete,
  onUploadStart,
  maxConcurrent = 5,
  className = '',
}) => {
  const [files, setFiles] = useState<UploadFile[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadQueueRef = useRef<Set<string>>(new Set());

  // Calculate SHA256 hash of file
  const calculateHash = async (file: File): Promise<string> => {
    const buffer = await file.arrayBuffer();
    const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    return hashHex;
  };

  // Upload a single file
  const uploadFile = async (uploadFile: UploadFile): Promise<void> => {
    const { file, id } = uploadFile;

    try {
      // Update status to uploading
      setFiles(prev => prev.map(f =>
        f.id === id ? { ...f, status: 'uploading', progress: 10 } : f
      ));

      // Calculate content hash
      const contentHash = await calculateHash(file);

      setFiles(prev => prev.map(f =>
        f.id === id ? { ...f, progress: 20, contentHash } : f
      ));

      // Get JWT token
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      if (!token) {
        throw new Error('Not authenticated');
      }

      // Request presigned upload URL
      const presignedResponse = await fetch(`${process.env.REACT_APP_API_URL}/photos/presigned-upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          filename: file.name,
          contentType: file.type || 'application/octet-stream',
        }),
      });

      if (!presignedResponse.ok) {
        throw new Error(`Failed to get upload URL: ${presignedResponse.statusText}`);
      }

      const { uploadUrl, key: s3Key } = await presignedResponse.json();

      setFiles(prev => prev.map(f =>
        f.id === id ? { ...f, progress: 30 } : f
      ));

      // Upload to S3 with progress tracking
      const xhr = new XMLHttpRequest();

      await new Promise<void>((resolve, reject) => {
        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable) {
            const percentComplete = 30 + Math.round((e.loaded / e.total) * 60);
            setFiles(prev => prev.map(f =>
              f.id === id ? { ...f, progress: percentComplete } : f
            ));
          }
        });

        xhr.addEventListener('load', () => {
          if (xhr.status === 200) {
            resolve();
          } else {
            reject(new Error(`Upload failed: ${xhr.status} ${xhr.statusText}`));
          }
        });

        xhr.addEventListener('error', () => reject(new Error('Network error during upload')));
        xhr.addEventListener('abort', () => reject(new Error('Upload aborted')));

        xhr.open('PUT', uploadUrl);
        xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
        xhr.send(file);
      });

      setFiles(prev => prev.map(f =>
        f.id === id ? { ...f, progress: 90 } : f
      ));

      // Create photo metadata record
      const metadataResponse = await fetch(`${process.env.REACT_APP_API_URL}/photos`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          filename: file.name,
          content_hash: contentHash,
          file_size: file.size,
          raw_path: s3Key,
        }),
      });

      if (!metadataResponse.ok) {
        console.warn('Metadata creation warning:', metadataResponse.statusText);
      }

      // Success!
      setFiles(prev => prev.map(f =>
        f.id === id ? { ...f, status: 'success', progress: 100 } : f
      ));

    } catch (error) {
      console.error(`Upload failed for ${file.name}:`, error);
      setFiles(prev => prev.map(f =>
        f.id === id ? {
          ...f,
          status: 'error',
          error: error instanceof Error ? error.message : 'Upload failed',
          progress: 0
        } : f
      ));
    } finally {
      uploadQueueRef.current.delete(id);
    }
  };

  // Process upload queue
  const processQueue = useCallback(async () => {
    const pendingFiles = files.filter(f =>
      f.status === 'pending' && !uploadQueueRef.current.has(f.id)
    );

    const availableSlots = maxConcurrent - uploadQueueRef.current.size;
    const filesToUpload = pendingFiles.slice(0, availableSlots);

    for (const file of filesToUpload) {
      uploadQueueRef.current.add(file.id);
      uploadFile(file); // Don't await - run concurrently
    }
  }, [files, maxConcurrent]);

  // Process queue when files change
  React.useEffect(() => {
    if (isUploading && files.some(f => f.status === 'pending')) {
      processQueue();
    } else if (isUploading && !files.some(f => f.status === 'pending' || f.status === 'uploading')) {
      // All done
      setIsUploading(false);
      const successCount = files.filter(f => f.status === 'success').length;
      onComplete?.(successCount);
    }
  }, [files, isUploading, processQueue, onComplete]);

  // Check if file is supported
  const isFileSupported = (file: File): boolean => {
    const ext = file.name.split('.').pop()?.toLowerCase();
    return ext ? SUPPORTED_EXTENSIONS.has(ext) : false;
  };

  // Handle file selection
  const handleFiles = useCallback((fileList: FileList | null) => {
    if (!fileList) return;

    const newFiles: UploadFile[] = [];
    const unsupportedFiles: string[] = [];

    Array.from(fileList).forEach(file => {
      if (isFileSupported(file)) {
        newFiles.push({
          file,
          id: `${file.name}-${Date.now()}-${Math.random()}`,
          status: 'pending',
          progress: 0,
        });
      } else {
        unsupportedFiles.push(file.name);
      }
    });

    if (unsupportedFiles.length > 0) {
      alert(`Unsupported files: ${unsupportedFiles.join(', ')}`);
    }

    if (newFiles.length > 0) {
      setFiles(prev => [...prev, ...newFiles]);
    }
  }, []);

  // Drag and drop handlers
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    handleFiles(e.dataTransfer.files);
  }, [handleFiles]);

  // File input change handler
  const handleFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    handleFiles(e.target.files);
  }, [handleFiles]);

  // Start upload
  const startUpload = useCallback(() => {
    if (files.length === 0) return;
    setIsUploading(true);
    onUploadStart?.();
  }, [files, onUploadStart]);

  // Retry failed uploads
  const retryFailed = useCallback(() => {
    setFiles(prev => prev.map(f =>
      f.status === 'error' ? { ...f, status: 'pending', progress: 0, error: undefined } : f
    ));
  }, []);

  // Remove a file
  const removeFile = useCallback((id: string) => {
    setFiles(prev => prev.filter(f => f.id !== id));
  }, []);

  // Clear all files
  const clearAll = useCallback(() => {
    setFiles([]);
    setIsUploading(false);
    uploadQueueRef.current.clear();
  }, []);

  // Calculate stats
  const stats = {
    total: files.length,
    pending: files.filter(f => f.status === 'pending').length,
    uploading: files.filter(f => f.status === 'uploading').length,
    success: files.filter(f => f.status === 'success').length,
    error: files.filter(f => f.status === 'error').length,
  };

  return (
    <div className={`max-w-4xl mx-auto ${className}`}>
      {/* Drop Zone */}
      {files.length === 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className={`relative border-2 border-dashed rounded-lg p-12 text-center transition-colors ${
            isDragging
              ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
              : 'border-neutral-300 dark:border-neutral-700 hover:border-neutral-400'
          }`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <CloudArrowUpIcon className="mx-auto h-16 w-16 text-neutral-400" />
          <h3 className="mt-4 text-lg font-semibold text-neutral-900 dark:text-white">
            Upload Your Photos
          </h3>
          <p className="mt-2 text-sm text-neutral-600 dark:text-neutral-400">
            Drag and drop your photos here, or click to browse
          </p>
          <p className="mt-1 text-xs text-neutral-500">
            Supports JPG, PNG, and all major RAW formats (CR3, NEF, ARW, DNG, etc.)
          </p>

          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*,.cr2,.cr3,.nef,.arw,.dng,.orf,.rw2,.pef,.srw,.raf"
            onChange={handleFileInputChange}
            className="hidden"
          />

          <button
            onClick={() => fileInputRef.current?.click()}
            className="mt-6 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Select Photos
          </button>
        </motion.div>
      )}

      {/* File List */}
      {files.length > 0 && (
        <div className="space-y-4">
          {/* Stats Header */}
          <div className="flex items-center justify-between bg-white dark:bg-neutral-800 rounded-lg p-4 shadow-sm">
            <div className="flex gap-6 text-sm">
              <span className="text-neutral-600 dark:text-neutral-400">
                Total: <strong className="text-neutral-900 dark:text-white">{stats.total}</strong>
              </span>
              {stats.uploading > 0 && (
                <span className="text-blue-600 dark:text-blue-400">
                  Uploading: <strong>{stats.uploading}</strong>
                </span>
              )}
              {stats.success > 0 && (
                <span className="text-green-600 dark:text-green-400">
                  Success: <strong>{stats.success}</strong>
                </span>
              )}
              {stats.error > 0 && (
                <span className="text-red-600 dark:text-red-400">
                  Failed: <strong>{stats.error}</strong>
                </span>
              )}
            </div>

            <div className="flex gap-2">
              {!isUploading && stats.pending > 0 && (
                <button
                  onClick={startUpload}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                >
                  Upload {stats.pending} {stats.pending === 1 ? 'Photo' : 'Photos'}
                </button>
              )}
              {stats.error > 0 && (
                <button
                  onClick={retryFailed}
                  className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors text-sm font-medium flex items-center gap-2"
                >
                  <ArrowPathIcon className="h-4 w-4" />
                  Retry Failed
                </button>
              )}
              <button
                onClick={clearAll}
                disabled={isUploading}
                className="px-4 py-2 text-neutral-700 dark:text-neutral-300 border border-neutral-300 dark:border-neutral-600 rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-700 transition-colors text-sm font-medium disabled:opacity-50"
              >
                Clear All
              </button>
            </div>
          </div>

          {/* File Items */}
          <div className="space-y-2 max-h-96 overflow-y-auto">
            <AnimatePresence mode="popLayout">
              {files.map(uploadFile => (
                <motion.div
                  key={uploadFile.id}
                  layout
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="bg-white dark:bg-neutral-800 rounded-lg p-4 shadow-sm"
                >
                  <div className="flex items-center gap-4">
                    {/* Status Icon */}
                    <div className="flex-shrink-0">
                      {uploadFile.status === 'success' && (
                        <CheckCircleIcon className="h-6 w-6 text-green-500" />
                      )}
                      {uploadFile.status === 'error' && (
                        <ExclamationCircleIcon className="h-6 w-6 text-red-500" />
                      )}
                      {uploadFile.status === 'uploading' && (
                        <div className="h-6 w-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                      )}
                      {uploadFile.status === 'pending' && (
                        <div className="h-6 w-6 rounded-full bg-neutral-200 dark:bg-neutral-700" />
                      )}
                    </div>

                    {/* File Info */}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-neutral-900 dark:text-white truncate">
                        {uploadFile.file.name}
                      </p>
                      <p className="text-xs text-neutral-500">
                        {(uploadFile.file.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                      {uploadFile.error && (
                        <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                          {uploadFile.error}
                        </p>
                      )}
                    </div>

                    {/* Progress */}
                    {uploadFile.status === 'uploading' && (
                      <div className="flex-shrink-0 w-24 text-right">
                        <p className="text-sm font-medium text-blue-600">
                          {uploadFile.progress}%
                        </p>
                      </div>
                    )}

                    {/* Remove Button */}
                    {uploadFile.status !== 'uploading' && (
                      <button
                        onClick={() => removeFile(uploadFile.id)}
                        className="flex-shrink-0 p-1 text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-200"
                      >
                        <XMarkIcon className="h-5 w-5" />
                      </button>
                    )}
                  </div>

                  {/* Progress Bar */}
                  {uploadFile.status === 'uploading' && (
                    <div className="mt-2 h-1 bg-neutral-200 dark:bg-neutral-700 rounded-full overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${uploadFile.progress}%` }}
                        className="h-full bg-blue-600"
                      />
                    </div>
                  )}
                </motion.div>
              ))}
            </AnimatePresence>
          </div>

          {/* Add More Button */}
          {!isUploading && (
            <button
              onClick={() => fileInputRef.current?.click()}
              className="w-full py-3 border-2 border-dashed border-neutral-300 dark:border-neutral-700 rounded-lg text-neutral-600 dark:text-neutral-400 hover:border-neutral-400 hover:text-neutral-700 transition-colors text-sm font-medium"
            >
              + Add More Photos
            </button>
          )}

          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*,.cr2,.cr3,.nef,.arw,.dng,.orf,.rw2,.pef,.srw,.raf"
            onChange={handleFileInputChange}
            className="hidden"
          />
        </div>
      )}
    </div>
  );
};

export default PhotoUploader;
